<script setup lang="ts">
import { ref, useTemplateRef } from 'vue';

import CustomButton from '@/components/CustomButton.vue';
import { redirectToStartPage } from '@/router';
import { useEmployeeStore } from '@/stores/employee';
import { processErrorWithFeedback } from '@/utils/error';
import { email, required } from '@/utils/validation';
import LoginBackground from '@/views/auth/LoginBackground.vue';
import { useI18n } from 'vue-i18n';

const employeeStore = useEmployeeStore();
const { t: translate } = useI18n();

const loading = ref(false);
const emailInput = ref('');
const isSubmitted = ref(false);
const form = useTemplateRef('form');

async function handleForgotPassword(): Promise<void> {
    const validationResult = await form.value?.validate();
    if (!validationResult?.valid) return;

    try {
        loading.value = true;
        await employeeStore.forgotPassword(emailInput.value);

        isSubmitted.value = true;
        form.value?.reset();
    } catch (error) {
        processErrorWithFeedback('De aanvraag voor het herstellen van het wachtwoord is mislukt', error);
    } finally {
        loading.value = false;
    }
}
</script>

<template>
    <LoginBackground>
        <div v-if="!isSubmitted">
            <h3>Wachtwoord vergeten?</h3>
            <p>Vul hieronder je e-mailadres in om een e-mail te ontvangen met een link om je wachtwoord te wijzigen</p>
            <v-form ref="form" class="w-100">
                <v-text-field
                    type="email"
                    name="email"
                    label="E-mailadres"
                    v-model="emailInput"
                    required
                    :rules="[required, email]"
                    @keydown.enter.prevent="handleForgotPassword"
                />
                <div class="d-flex align-center justify-center mt-10">
                    <CustomButton :loading="loading" text="Verzend link" color="main-dark" @click="handleForgotPassword" />
                </div>
            </v-form>
        </div>
        <div v-else>
            <h3>Instructies verzonden</h3>
            <p class="d-flex align-center justify-center">
                {{ translate('success.ForgotPasswordSent') }}
            </p>
            <div class="d-flex align-center justify-center mt-10">
                <CustomButton class="px-10" text="Terug naar de inlogpagina" color="main-dark" @click="redirectToStartPage()" />
            </div>
        </div>
    </LoginBackground>
</template>
